/**
 * API for sets and cards stored in the database (TCG catalog).
 */

import type { FindCardResult, FullPokemonCard, Locale } from '@/types/pokemon'
import type { CardmarketNonSingle, CardmarketSingleWithPrice } from '../../../server/src/carddetector/types'
import type { CardmarketExpansionListResponse, CardmarketExpansionRow, CardsListResponse, Set, SetsListResponse } from '../types/cardCatalog'
import { getApiUrl } from '@/lib/utils'

const API_URL = getApiUrl()

/**
 * Parses a single value from JSON (ISO string or already a Date) into a {@link Date}, or `null` if missing/invalid.
 */
export function parseJsonDate(value: unknown): Date | null {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

function parseRequiredJsonDate(value: unknown): Date {
  return parseJsonDate(value) ?? new Date(0)
}

/**
 * Converts ISO date strings on a catalog {@link Set} (from `res.json()`) into {@link Date} instances.
 */
export function parseSetDatesFromRest(raw: Set): Set {
  return {
    ...raw,
    releasedAt: parseJsonDate(raw.releasedAt as unknown),
    createdAt: parseRequiredJsonDate(raw.createdAt as unknown),
    updatedAt: parseRequiredJsonDate(raw.updatedAt as unknown),
  }
}

export function parseCardmarketExpansionFromRest(raw: CardmarketExpansionRow): CardmarketExpansionRow {
  return {
    ...raw,
    createdAt: parseRequiredJsonDate(raw.createdAt as unknown),
    updatedAt: parseRequiredJsonDate(raw.updatedAt as unknown),
    sets: (raw.sets ?? []).map((s) => parseSetDatesFromRest(s)),
  }
}

/**
 * Converts ISO date strings on a catalog card and its nested set (from `res.json()`) into {@link Date} instances.
 */
export function parseFullPokemonCardDatesFromRest(raw: FullPokemonCard): FullPokemonCard {
  return {
    ...raw,
    createdAt: parseRequiredJsonDate(raw.createdAt as unknown),
    updatedAt: parseRequiredJsonDate(raw.updatedAt as unknown),
    set: parseSetDatesFromRest(raw.set),
  }
}

function getMutationFetchOptions(method: string, body?: unknown): RequestInit {
  return {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v))
  }
  const q = search.toString()
  return q ? `?${q}` : ''
}

export async function fetchSets(
  options: {
    limit?: number
    offset?: number
    locale?: Locale
    q?: string
  } = {},
): Promise<SetsListResponse> {
  const query = buildQuery({
    limit: options.limit,
    offset: options.offset,
    locale: options.locale,
    q: options.q?.trim(),
  })
  const response = await fetch(`${API_URL}/api/cards/sets${query}`)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to load sets (${response.status})`)
  }
  const body = data as SetsListResponse
  return {
    ...body,
    items: body.items.map(parseSetDatesFromRest),
  }
}

/**
 * Sets the catalog set's alternative (e.g. English) name. Requires a logged-in session.
 */
export async function updateSetAlternativeName(setId: string, alternativeName: string | null): Promise<Set> {
  const response = await fetch(`${API_URL}/api/cards/sets/${encodeURIComponent(setId)}`, getMutationFetchOptions('PATCH', { alternativeName }))
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to update set (${response.status})`)
  }
  return parseSetDatesFromRest(data as Set)
}

export async function fetchCardmarketExpansions(
  options: {
    limit?: number
    offset?: number
    q?: string
  } = {},
): Promise<CardmarketExpansionListResponse> {
  const query = buildQuery({
    limit: options.limit,
    offset: options.offset,
    q: options.q?.trim(),
  })
  const response = await fetch(`${API_URL}/api/cards/cardmarket-expansions${query}`)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to load Cardmarket expansions (${response.status})`)
  }
  const body = data as CardmarketExpansionListResponse
  return {
    ...body,
    items: body.items.map(parseCardmarketExpansionFromRest),
  }
}

export async function updateCardmarketExpansion(id: string, input: { name?: string | null; setIds?: string[] }): Promise<CardmarketExpansionRow> {
  const response = await fetch(`${API_URL}/api/cards/cardmarket-expansions/${encodeURIComponent(id)}`, getMutationFetchOptions('PATCH', input))
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to update Cardmarket expansion (${response.status})`)
  }
  return parseCardmarketExpansionFromRest(data as CardmarketExpansionRow)
}

export type CardmarketSinglesListResponse = {
  version: string
  createdAt: string
  total: number
  limit: number
  offset: number
  items: CardmarketSingleWithPrice[]
}

export type CardmarketNonSinglesListResponse = {
  version: string
  createdAt: string
  total: number
  limit: number
  offset: number
  items: CardmarketNonSingle[]
}

export async function fetchCardmarketSinglesList(params: { q?: string; limit?: number; offset?: number; idExpansion?: number } = {}): Promise<CardmarketSinglesListResponse> {
  const query = buildQuery({
    q: params.q?.trim(),
    limit: params.limit,
    offset: params.offset,
    idExpansion: params.idExpansion,
  })
  const response = await fetch(`${API_URL}/api/cardmarket/singles${query}`)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to load Cardmarket singles (${response.status})`)
  }
  return data as CardmarketSinglesListResponse
}

export async function fetchCardmarketNonSinglesList(params: { q?: string; limit?: number; offset?: number; idExpansion?: number } = {}): Promise<CardmarketNonSinglesListResponse> {
  const query = buildQuery({
    q: params.q?.trim(),
    limit: params.limit,
    offset: params.offset,
    idExpansion: params.idExpansion,
  })
  const response = await fetch(`${API_URL}/api/cardmarket/nonsingles${query}`)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to load Cardmarket non-singles (${response.status})`)
  }
  return data as CardmarketNonSinglesListResponse
}

export async function fetchCards(
  options: {
    limit?: number
    offset?: number
    setId?: string
    locale?: Locale
    q?: string
  } = {},
): Promise<CardsListResponse> {
  const query = buildQuery({
    limit: options.limit,
    offset: options.offset,
    setId: options.setId,
    locale: options.locale,
    q: options.q?.trim(),
  })
  const response = await fetch(`${API_URL}/api/cards${query}`)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to load cards (${response.status})`)
  }
  const body = data as CardsListResponse
  return {
    ...body,
    items: body.items.map(parseFullPokemonCardDatesFromRest),
  }
}

export async function analyzeCard(image: Blob): Promise<FindCardResult> {
  const response = await fetch(`${API_URL}/api/analyze-card`, {
    method: 'POST',
    credentials: 'include',
    body: image,
  })
  const data = (await response.json().catch(() => ({}))) as FindCardResult
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to analyze card (${response.status})`)
  }

  const result = {
    bestMatch: data.bestMatch ? parseFullPokemonCardDatesFromRest(data.bestMatch) : undefined,
    possibleCards: data.possibleCards.map(parseFullPokemonCardDatesFromRest),
  }

  return result
}
